import Box from '@mui/material/Box'
import LeftPanelComponent from './LeftPanelComponent'
import RightPanelComponent from './RightPanelComponent'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CircularProgress, Stack, TextField } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { architectureRoles, engineeringRoles } from '../../models/AccessControl'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import {
  setSearchFilter,
  updateSelectedClient,
  updateSelectedFile,
  updateSelectedProject,
} from '../../actions/organization'
import { createSelector } from '@reduxjs/toolkit'
import { setSelectedProject } from '../../actions/review'

const clientsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.clients,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)
const filesSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.files,
)
const engProjectsSelector = createSelector(
  [(state: RootState) => state.review],
  (review) => review.assignedProjects,
)
const searchFilterSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.searchFilter,
)

interface LayoutWithPanelsProps {}

const LayoutWithPanels: React.FC<LayoutWithPanelsProps> = () => {
  const boxRef = useRef<HTMLDivElement>(null)
  const [boxHeight, setBoxHeight] = useState(0)

  const location = useLocation()
  const { clientId, projectId, fileId } = useParams<{
    clientId: string
    projectId: string
    fileId: string
  }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)
  const clients = useSelector(clientsSelector)
  const architectureProjects = useSelector(projectsSelector)
  const engineeringProjects = useSelector(engProjectsSelector)
  const files = useSelector(filesSelector)
  const searchFilter = useSelector(searchFilterSelector)

  const pathItems = location.pathname.split('/')
  const isFilePage = user
    ? architectureRoles.includes(user?.role.name)
      ? (pathItems.length > 4 &&
          pathItems[1] === 'projects' &&
          pathItems[3] === 'files') ||
        pathItems.includes('uploadFiles')
      : engineeringRoles.includes(user?.role.name)
        ? pathItems.length > 2 && pathItems[1] === 'projects' && !!fileId
        : false
    : false

  const routeEndsWithTrailingSlash = location.pathname.endsWith('/')

  const handleSearchFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchFilter(event?.target.value))
  }

  useEffect(() => {
    const calculateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    if (location.pathname !== '/' && routeEndsWithTrailingSlash) {
      navigate(location.pathname.slice(0, -1))
    }

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  useEffect(() => {
    if (user && architectureRoles.includes(user?.role.name) && !isFilePage) {
      const parsedClientId = clientId && Number.parseFloat(clientId)
      const client = clients.find((client) => client.id === parsedClientId)
      dispatch(updateSelectedClient(client))

      const parsedProjectId = projectId && Number.parseFloat(projectId)
      const project = architectureProjects.find(
        (project) => project.id === parsedProjectId,
      )
      dispatch(updateSelectedProject(project))
      if (project && !client) {
        const client = clients.find((client) => client.id === project.client_id)
        dispatch(updateSelectedClient(client))
      }

      const parsedFileId = fileId && Number.parseFloat(fileId)
      const file = files.find((file) => file.id === parsedFileId)
      if (file) {
        dispatch(updateSelectedFile(file))
      }
    }

    if (user && engineeringRoles.includes(user?.role.name) && !isFilePage) {
      const parsedFileId = fileId && Number.parseFloat(fileId)
      const project = engineeringProjects.find(
        (project) => project.file_id === parsedFileId,
      )
      dispatch(setSelectedProject(project?.id))
    }
  }, [location.pathname])

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        boxSizing: 'border-box',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {!!!user ? (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          width='100%'
          height='100%'
        >
          <CircularProgress size={200} sx={{ color: '#1705E5' }} />
        </Box>
      ) : (
        <>
          {!isFilePage ? (
            <>
              <Box
                display='flex'
                justifyContent='flex-start'
                width={'18%'}
                m={'10px'}
              >
                <LeftPanelComponent />
              </Box>
              <Stack
                display='flex'
                flexDirection='column'
                flex={1}
                height='100%'
              >
                {!!user && architectureRoles.includes(user?.role.name) && (
                  <Box
                    ref={boxRef}
                    display='flex'
                    justifyContent='flex-end'
                    pt='1rem'
                    boxSizing='border-box'
                  >
                    <TextField
                      sx={{
                        width: '33%',
                        my: 0,
                        '& .MuiInputBase-input': {
                          color: '#04002E',
                          fontSize: '0.7rem',
                          lineHeight: 'normal',
                        },
                        '& .MuiOutlinedInput-root': {
                          boxShadow:
                            '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#C3C3C3',
                          opacity: 1,
                          fontSize: '0.7rem',
                        },
                      }}
                      size='small'
                      placeholder='Search for projects and documentation'
                      margin='dense'
                      value={searchFilter}
                      onChange={handleSearchFilterChange}
                    />
                  </Box>
                )}
                <Box
                  display='flex'
                  flexDirection='column'
                  pt={'5vh'}
                  pb={'1vh'}
                  height={`calc(100% - ${boxHeight}px)`}
                  boxSizing='border-box'
                >
                  <Outlet />
                </Box>
              </Stack>
              <Box
                display='flex'
                justifyContent='flex-end'
                width={'20%'}
                m={'10px'}
              >
                <RightPanelComponent />
              </Box>
            </>
          ) : (
            <>
              {!routeEndsWithTrailingSlash && (
                <Box
                  display='flex'
                  flexDirection='column'
                  height='100%'
                  width='100%'
                >
                  <Outlet />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default LayoutWithPanels
